import React, { useEffect, Suspense, useState, useCallback } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppState } from "./stores";
import { verifyTokenSync, apiGetLogoUrl } from "./stores/user/user.store";
import { ErrorBoundary } from "react-error-boundary";
import PageLoading from "./components/Loading/Loading";
import { setGlobalNavigate } from "./utils/navigate";
import { PaymentTypeProvider } from 'src/contexts/PaymentTypeProvider';
import IpWhitelistError from "./pages/ipWhitelistError";

const PublicLayout = React.lazy(() => import("src/layout/PublicLayout/PublicLayout"));
const PrivateLayout = React.lazy(() => import("src/layout/PrivateLayout/PrivateLayout"));

function App() {
  const dispatch = useAppDispatch();
  const user = useAppState(state => state.user);
  const navigate = useNavigate();
  const [ipCheckLoading, setIpCheckLoading] = useState(true);
  const [ipWhitelistError, setIpWhitelistError] = useState(false);

  useEffect(() => {
    setGlobalNavigate(navigate); // 初始化全局 navigate
  }, [navigate]);

  // IP 白名單檢查函數
  const checkIpWhitelist = useCallback(async () => {
    try {
      const response = await apiGetLogoUrl();
      // 檢查 rsCode 是否為 ssoError（可能是字符串或數字）
      if (String(response?.rsCode) === 'ssoError') {
        setIpWhitelistError(true);
      } else {
        // 如果檢查通過，清除錯誤狀態
        setIpWhitelistError(false);
        // IP 通過時，如果有 rsData 就存儲到 localStorage
        if (response?.rsData) {
          localStorage.setItem('loginLogoUrl', response.rsData);
        }
      }
    } catch (error) {
      console.error('IP whitelist check failed:', error);
      // 如果檢查失敗，為了安全起見，可以設置為錯誤狀態
      // 或者根據業務需求決定是否允許繼續
    } finally {
      setIpCheckLoading(false);
    }
  }, []);

  // IP 白名單檢查 - 在所有其他邏輯之前執行
  useEffect(() => {
    checkIpWhitelist();
  }, [checkIpWhitelist]);

  // 監聽頁面可見性變化，當頁面重新可見時重新檢查
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && ipWhitelistError) {
        // 頁面重新可見且當前處於 IP 白名單錯誤狀態時，重新檢查
        setIpCheckLoading(true);
        checkIpWhitelist();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [ipWhitelistError, checkIpWhitelist]);

  useEffect(() => {
    async function init() {
      await dispatch(verifyTokenSync());
    }
    // 只有在 IP 檢查通過後才執行 token 驗證
    if (!ipCheckLoading && !ipWhitelistError) {
      init();
    }
  }, [dispatch, ipCheckLoading, ipWhitelistError]);

  // 如果 IP 未加入白名單，顯示錯誤頁面，阻止所有路由渲染
  if (ipWhitelistError) {
    return <IpWhitelistError />;
  }

  // 在 IP 檢查完成前顯示加載畫面
  if (ipCheckLoading) {
    return <PageLoading />;
  }

  return (
    <PaymentTypeProvider>
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <div>
            <h2>Something went wrong:</h2>
            <p>{error.message}</p>
          </div>
        )}
      >
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* 公共路由 */}
            <Route key={"public"} path="/account/*" element={<PublicLayout />} />
            {/* 私有路由或重定向 */}
            {user.api && user.cachekeyApp1 && user.chyjrtgnApp1 && user.platformsessionId && user.uid ? (
              <>
                <Route path="/*" element={<PrivateLayout />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to={"/account/login"} replace />} />
            )}
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </PaymentTypeProvider>
  );
}

export default App;
